import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS } from "@/data";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenis = useLenis();

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { offset: -70 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "glass border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1500px] items-center justify-between px-6 py-5 md:px-12">
        <button
          data-testid="logo"
          onClick={() => scrollTo("#top")}
          className="group flex items-center gap-2.5"
        >
          <span className="h-2.5 w-2.5 rotate-45 bg-primary transition-transform duration-300 group-hover:rotate-[135deg]" />
          <span className="font-cabinet text-lg font-black uppercase leading-none tracking-tight text-white">
            Velocity<span className="text-primary">.</span>
          </span>
        </button>

        <div className="hidden items-center gap-10 md:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => scrollTo(l.href)}
              className="font-mono-x text-xs uppercase tracking-widest text-zinc-400 transition-colors duration-300 hover:text-white"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-book-cta"
            onClick={() => scrollTo("#booking")}
            className="border border-primary gradient-fill px-6 py-3 font-mono-x text-xs uppercase tracking-widest text-black transition-opacity duration-300 hover:opacity-90"
          >
            Book Now
          </button>
        </div>

        <button
          data-testid="menu-toggle"
          onClick={() => setOpen(!open)}
          className="text-white md:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-white/10 glass md:hidden"
          >
            <div className="flex flex-col px-6 py-6">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  onClick={() => { scrollTo(l.href); setOpen(false); }}
                  className="border-b border-white/5 py-4 text-left font-cabinet text-2xl font-bold uppercase text-white"
                >
                  {l.label}
                </button>
              ))}
              <a
                href="tel:+18125834516"
                data-testid="mobile-call"
                className="mt-5 flex items-center justify-center gap-3 gradient-fill px-6 py-4 font-mono-x text-xs uppercase tracking-widest text-black"
              >
                <Phone size={16} /> Call (812) 583-4516
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
